import { Menu, MenuItem } from "@mui/material";
import { OLButton } from "OLButton";
import { ReactComponent as ChevronLeftIcon } from "components/Icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "components/Icons/chevron-right.svg";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { useEffect, useRef, useState } from "react";
import { Translation } from "reduxState/translations/translationsSlice";
import { RewardFields } from "types/rewards";
import { TranslationTab } from "./TranslationTab";
import { useStyles } from "./styles";

interface Props {
  activeTranslation: number;
  setActiveTranslation: (index: number) => void;
  translations: RewardFields["translations"];
  availableLanguages: Translation[];
  push: ({
    code,
    languageName,
  }: {
    code: string;
    languageName: string;
  }) => void;
  setFieldValue: (field: string, value: any[]) => void;
  isEditMode?: boolean;
}

export const TranslationTabs = ({
  activeTranslation,
  setActiveTranslation,
  translations,
  availableLanguages,
  push,
  setFieldValue,
  isEditMode,
}: Props) => {
  const [isFirstElemVisible, setIsFirstElemVisible] = useState(true);
  const [isLastElemVisible, setIsLastElemVisible] = useState(true);
  const refButtons = useRef(null);
  const classes = useStyles();
  const { message } = useMessage();
  const contextMenu = useContextMenu();
  const translationsCount = translations.length;
  const visibleTranslations = translations.slice(1);
  const areAllTranslationsAdded =
    translations.length >= availableLanguages.length;

  useEffect(() => {
    const firstElem = refButtons && (refButtons as any).current.firstChild;
    const lastElem = refButtons && (refButtons as any).current.lastChild;

    const options = {
      root: refButtons.current,
      rootMargin: "0px",
      threshold: 1,
    };

    const observerFirst = new IntersectionObserver((entries) => {
      const [entry] = entries;

      setIsFirstElemVisible(entry.isIntersecting);
    }, options);

    const observerLast = new IntersectionObserver((entries) => {
      const [entry] = entries;
      setIsLastElemVisible(entry.isIntersecting);
    }, options);

    observerFirst.observe(firstElem);
    observerLast.observe(lastElem);

    return () => {
      observerFirst.unobserve(firstElem);
      observerLast.unobserve(lastElem);
    };
  }, [translations]);

  useEffect(() => {
    const activeRuleElement = document.getElementById(
      `tab-${activeTranslation}`,
    );

    if (refButtons && refButtons.current && activeRuleElement) {
      (refButtons.current as Element).scrollTo({
        left: activeRuleElement.offsetLeft - activeRuleElement.offsetWidth / 2,
        top: 0,
        behavior: "smooth",
      });
    }
  }, [activeTranslation]);

  const getTabName = (
    translation: RewardFields["translations"][number],
    availableLanguages: Translation[],
    isEditMode: boolean,
  ) => {
    if (!isEditMode) {
      return `${translation?.languageName} (${translation?.code})`;
    }

    const matchedLanguage = availableLanguages.find(
      (language) => language.code === (translation.locale || translation.code),
    );

    if (matchedLanguage?.name && matchedLanguage?.code) {
      return `${matchedLanguage.name} (${matchedLanguage.code})`;
    }

    return translation.locale as string;
  };

  return (
    <div className={classes.menuContainer}>
      <div className={classes.menuWrapper}>
        <div className={classes.menu}>
          <div ref={refButtons} className={classes.menuTabsWrapper}>
            {visibleTranslations.map((translation, i) => {
              const idx = i + 1;
              const isSelected = activeTranslation === idx;
              const tabName = getTabName(
                translation,
                availableLanguages,
                isEditMode as boolean,
              );

              return (
                <TranslationTab
                  key={isEditMode ? translation?.locale : translation?.code}
                  isSelected={isSelected}
                  idx={idx}
                  name={tabName}
                  setActiveTranslation={setActiveTranslation}
                  onDelete={() => {
                    const updated = translations.filter((_, j) => j !== idx);

                    setFieldValue("translations", updated);
                    setActiveTranslation(
                      activeTranslation === 1 ? 1 : activeTranslation - 1,
                    );
                  }}
                  data-test-id={`menu-item-${idx}`}
                />
              );
            })}
          </div>
          {!isFirstElemVisible && (
            <div
              className={classes.arrowSlider}
              style={{ left: 0 }}
              onClick={() => {
                if (refButtons.current) {
                  (refButtons.current as Element).scrollBy({
                    top: 0,
                    left: -100,
                    behavior: "smooth",
                  });
                }
              }}
              data-test-id="menu-left"
            >
              <div className={classes.iconWrapper}>
                <ChevronLeftIcon />
              </div>
            </div>
          )}
          {!isLastElemVisible && (
            <div
              className={classes.arrowSlider}
              style={{ right: 0 }}
              onClick={() => {
                if (refButtons.current) {
                  (refButtons.current as Element).scrollBy({
                    top: 0,
                    left: 100,
                    behavior: "smooth",
                  });
                }
              }}
              data-test-id="menu-right"
            >
              <div className={classes.iconWrapper}>
                <ChevronRightIcon />
              </div>
            </div>
          )}
        </div>
        <OLButton
          onClick={
            availableLanguages.filter(
              (lang) =>
                !translations.find(
                  (openLang) =>
                    (openLang?.code || openLang?.locale) === lang?.code,
                ),
            ).length
              ? contextMenu.handleMenuOpen
              : undefined
          }
          startIcon={<PlusIcon />}
          disabled={areAllTranslationsAdded}
          data-test-id="add-translation"
          size="small"
          className={classes.button}
        >
          {message({ id: "addTranslation" })}
        </OLButton>
        <Menu
          anchorEl={contextMenu.anchor}
          anchorOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          onClose={contextMenu.handleMenuClose}
          open={contextMenu.isMenuOpen}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
        >
          {availableLanguages
            .filter(
              (lang) =>
                !translations.find(
                  (openLang) =>
                    (openLang.code || openLang.locale) === lang.code,
                ),
            )
            .map((language) => (
              <MenuItem
                key={language.code}
                onClick={() => {
                  contextMenu.handleMenuClose();
                  push({
                    code: language.code,
                    languageName: language.name,
                  });
                  setActiveTranslation(translationsCount);
                }}
              >
                {language.name}
              </MenuItem>
            ))}
        </Menu>
      </div>
    </div>
  );
};
